import React, { useRef, useState } from 'react'
import {
	Box,
	Paper,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Button,
	Alert,
	Snackbar,
	TextField,
	Tooltip,
	IconButton,
	useTheme,
	useMediaQuery,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Edit, Save, Cancel, ClearAll } from '@mui/icons-material'
import { BetoneFormProps } from './types'
import { useBetoneForm } from './hooks/useBetoneForm'
import { getSortedFields } from './utils/sectionHelpers'
import { FormSection } from './components/FormSection'
import { SubmitButton } from './components/SubmitButton'
import { ScrollToTopButton } from './components/ScrollToTopButton'
import { FormResult } from './components/FormResult'
import SimpleMobileBetoneForm from './components/SimpleMobileBetoneForm'
import { PeriodSubmissionToggle } from './components/PeriodSubmissionToggle'
import { PeriodDatePicker } from './components/PeriodDatePicker'

import { LinkedFields } from '../LinkedFields'
import { FormFieldService } from '../../../services/formFieldService'
import { usePeriodSubmission } from './hooks/usePeriodSubmission'

const BetoneForm: React.FC<BetoneFormProps> = ({
	form,
	fields,
	editData,
	preloadedOptions,
	isAdminMode = false,
	onFieldUpdate,
}) => {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
	const isTablet = useMediaQuery(theme.breakpoints.down('md'))

	const formRef = useRef<HTMLDivElement>(null)
	const [snackbar, setSnackbar] = useState<{
		open: boolean
		message: string
		severity?: 'success' | 'error'
	}>({
		open: false,
		message: '',
	})

	// Состояние для редактирования заголовков в аккордеонах
	const [editingSection, setEditingSection] = useState<number | null>(null)
	const [tempTitle, setTempTitle] = useState('')

	const {
		formik,
		submitting,
		submitResult,
		clearSubmitResult,
		resetForm,
		clearSection,
		fieldSections,
		useSectionMode,
		toggleSectionExpanded,
		isSectionExpanded,
		expandAllSections,
		collapseAllSections,
		showScrollTop,
		scrollToTop,
		handleFieldChange,
		getFieldError,
	} = useBetoneForm(
		form.id || form._id || '',
		fields,
		editData,
		preloadedOptions
	)

	// Хук для периодических заявок
	const {
		isPeriodMode,
		periodStartDate,
		periodEndDate,
		dateFieldName,
		dateRangeError,
		hasDateField,
		togglePeriodMode,
		setPeriodStartDate,
		setPeriodEndDate,
		submitPeriodSubmissions,
	} = usePeriodSubmission(fields)

	// Автоматическое переключение на мобильную версию для небольших экранов
	if (isMobile || isTablet) {
		return (
			<SimpleMobileBetoneForm
				form={form}
				fields={fields}
				editData={editData}
				preloadedOptions={preloadedOptions}
				isAdminMode={isAdminMode}
				onFieldUpdate={onFieldUpdate}
			/>
		)
	}

	// Обработчик изменения названия раздела
	const handleSectionTitleChange = async (
		sectionId: string,
		newTitle: string
	) => {
		try {
			await FormFieldService.updateSectionTitle(sectionId, newTitle)
			setSnackbar({
				open: true,
				message: 'Название раздела успешно обновлено',
				severity: 'success',
			})

			// Обновляем локальное состояние через пропс
			if (onFieldUpdate) {
				onFieldUpdate(sectionId, { label: newTitle })
			}
		} catch (error) {
			console.error('Ошибка при обновлении названия раздела:', error)
			setSnackbar({
				open: true,
				message: 'Ошибка при обновлении названия раздела',
				severity: 'error',
			})
		}
	}

	// Обработчики для редактирования в аккордеонах
	const handleStartEditingAccordion = (
		index: number,
		currentTitle: string,
		event: React.MouseEvent
	) => {
		event.stopPropagation() // Предотвращаем сворачивание/разворачивание аккордеона
		setEditingSection(index)
		setTempTitle(currentTitle)
	}

	const handleSaveAccordionTitle = async (
		section: any,
		index: number,
		event?: React.MouseEvent
	) => {
		if (event) {
			event.stopPropagation()
		}

		if (section.id && tempTitle.trim() && tempTitle !== section.title) {
			await handleSectionTitleChange(section.id, tempTitle.trim())
		}
		setEditingSection(null)
		setTempTitle('')
	}

	const handleCancelEditingAccordion = (event?: React.MouseEvent) => {
		if (event) {
			event.stopPropagation()
		}
		setEditingSection(null)
		setTempTitle('')
	}

	const handleKeyPressAccordion = (
		event: React.KeyboardEvent,
		section: any,
		index: number
	) => {
		if (event.key === 'Enter') {
			event.preventDefault()
			handleSaveAccordionTitle(section, index)
		} else if (event.key === 'Escape') {
			event.preventDefault()
			handleCancelEditingAccordion()
		}
	}

	// Обработчик отправки формы с поддержкой периодических заявок
	const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()

		// Если включен режим периодических заявок и нет ошибок валидации дат
		if (isPeriodMode && !dateRangeError && !editData?.submissionId) {
			// Используем стандартную валидацию formik для полей формы
			const errors = await formik.validateForm()
			if (Object.keys(errors).length > 0) {
				formik.setTouched(
					Object.keys(errors).reduce((acc, key) => {
						acc[key] = true
						return acc
					}, {} as any)
				)
				setSnackbar({
					open: true,
					message: 'Пожалуйста, исправьте ошибки в форме',
					severity: 'error',
				})
				return
			}

			// Отправка периодических заявок
			try {
				const result = await submitPeriodSubmissions(
					form.id || form._id || '',
					formik.values
				)

				if (result.success) {
					setSnackbar({
						open: true,
						message: result.message,
						severity: 'success',
					})

					// Сбрасываем форму и переключаем режим
					formik.resetForm()
					togglePeriodMode(false)

					// Перенаправление после небольшой задержки
					setTimeout(() => {
						window.location.href = '/my-submissions'
					}, 2000)
				}
			} catch (error: any) {
				setSnackbar({
					open: true,
					message: error.response?.data?.message || 'Ошибка создания заявок',
					severity: 'error',
				})
			}
		} else {
			// Обычная отправка формы
			formik.handleSubmit(e)
		}
	}

	return (
		<Box component='form' onSubmit={handleFormSubmit}>
			<Box sx={{ maxWidth: '800px', mx: 'auto', p: { xs: 1, sm: 2 } }}>
				<Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
					{/* Заголовок формы */}
					<Box sx={{ p: 3, bgcolor: 'primary.main', color: 'white' }}>
						<Typography variant='h4' component='h1' gutterBottom>
							{form.title}
						</Typography>
						{form.description && (
							<Typography variant='body1' sx={{ opacity: 0.9 }}>
								{form.description}
							</Typography>
						)}
					</Box>

					{/* Уведомление о копировании */}
					{editData?.isCopy && (
						<Box sx={{ p: 3, pb: 0 }}>
							<Alert severity='info' sx={{ mb: 2 }}>
								<Typography variant='body2'>
									Вы копируете заявку "{editData.originalTitle}" (№
									{editData.originalSubmissionNumber}). Данные предзаполнены, но
									будет создана новая заявка.
								</Typography>
							</Alert>
						</Box>
					)}

					{/* Компоненты периодических заявок - только для новых заявок */}
					{!editData?.submissionId && (
						<Box sx={{ p: 3, pb: 0 }}>
							<PeriodSubmissionToggle
								enabled={isPeriodMode}
								onToggle={togglePeriodMode}
								dateFieldName={dateFieldName}
								hasDateField={hasDateField}
							/>

							{isPeriodMode && hasDateField && (
								<PeriodDatePicker
									startDate={periodStartDate}
									endDate={periodEndDate}
									onStartDateChange={setPeriodStartDate}
									onEndDateChange={setPeriodEndDate}
									error={dateRangeError || undefined}
								/>
							)}
						</Box>
					)}

					{/* Результат отправки */}
					{submitResult && (
						<Box sx={{ p: 3, pb: 0 }}>
							<FormResult
								result={submitResult}
								onClose={clearSubmitResult}
								onReset={resetForm}
							/>
						</Box>
					)}

					{/* Содержимое формы */}
					<Box ref={formRef}>
						{useSectionMode ? (
							// Секционный режим с аккордеонами
							<Box sx={{ p: 3 }}>
								{/* Кнопки управления секциями */}
								<Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
									<Button
										size='small'
										variant='outlined'
										onClick={expandAllSections}
									>
										Развернуть все
									</Button>
									<Button
										size='small'
										variant='outlined'
										onClick={collapseAllSections}
									>
										Свернуть все
									</Button>
								</Box>

								{/* Секции как аккордеоны */}
								{fieldSections.map((section, index) => (
									<Accordion
										key={index}
										expanded={isSectionExpanded(index)}
										onChange={() =>
											editingSection !== index && toggleSectionExpanded(index)
										}
										sx={{ mb: 1 }}
									>
										<AccordionSummary
											expandIcon={<ExpandMoreIcon />}
											sx={{
												'&:hover': {
													backgroundColor: 'rgba(0, 0, 0, 0.04)',
												},
											}}
										>
											{editingSection === index && isAdminMode ? (
												<Box
													sx={{
														display: 'flex',
														alignItems: 'center',
														gap: 1,
														flex: 1,
														mr: 2,
													}}
													onClick={e => e.stopPropagation()}
												>
													<TextField
														value={tempTitle}
														onChange={e => setTempTitle(e.target.value)}
														onKeyDown={e =>
															handleKeyPressAccordion(e, section, index)
														}
														variant='outlined'
														size='small'
														autoFocus
														sx={{
															flex: 1,
															'& .MuiOutlinedInput-root': {
																fontSize: '1.25rem',
																fontWeight: 600,
															},
														}}
													/>
													<Tooltip title='Сохранить'>
														<Box
															onClick={e =>
																handleSaveAccordionTitle(section, index, e)
															}
															sx={{
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'center',
																width: 32,
																height: 32,
																borderRadius: 1,
																cursor: 'pointer',
																color: 'primary.main',
																'&:hover': {
																	backgroundColor: 'primary.main',
																	color: 'white',
																},
															}}
														>
															<Save fontSize='small' />
														</Box>
													</Tooltip>
													<Tooltip title='Отменить'>
														<Box
															onClick={handleCancelEditingAccordion}
															sx={{
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'center',
																width: 32,
																height: 32,
																borderRadius: 1,
																cursor: 'pointer',
																color: 'secondary.main',
																'&:hover': {
																	backgroundColor: 'secondary.main',
																	color: 'white',
																},
															}}
														>
															<Cancel fontSize='small' />
														</Box>
													</Tooltip>
												</Box>
											) : (
												<Box
													sx={{
														display: 'flex',
														alignItems: 'center',
														flex: 1,
													}}
												>
													<Typography
														variant='h6'
														component='h3'
														sx={{ flex: 1 }}
													>
														{section.title}
													</Typography>
													<Typography
														variant='body2'
														sx={{ ml: 2, color: 'text.secondary' }}
													>
														(
														{(() => {
															// Считаем только поля для ввода (исключаем header, divider)
															const inputFields = section.fields.filter(
																field =>
																	field.type !== 'header' &&
																	field.type !== 'divider'
															)
															// Считаем заполненные поля
															const filledFields = inputFields.filter(field => {
																const value = formik.values[field.name]
																return (
																	value !== undefined &&
																	value !== null &&
																	value !== ''
																)
															})

															return `${filledFields.length}/${inputFields.length} заполнено`
														})()}
														)
													</Typography>
													{/* Кнопка очистки секции */}
													<Tooltip title='Очистить раздел'>
														<IconButton
															onClick={e => {
																e.stopPropagation()
																if (
																	window.confirm(
																		`Вы уверены, что хотите очистить все поля в разделе "${section.title}"? Это действие нельзя отменить.`
																	)
																) {
																	clearSection(index)
																}
															}}
															size='small'
															color='warning'
															sx={{ ml: 1 }}
														>
															<ClearAll />
														</IconButton>
													</Tooltip>
													{isAdminMode && section.id && (
														<Tooltip title='Редактировать название раздела'>
															<Box
																onClick={e =>
																	handleStartEditingAccordion(
																		index,
																		section.title,
																		e
																	)
																}
																sx={{
																	display: 'flex',
																	alignItems: 'center',
																	justifyContent: 'center',
																	width: 32,
																	height: 32,
																	borderRadius: 1,
																	cursor: 'pointer',
																	ml: 1,
																	opacity: 0.7,
																	'&:hover': {
																		opacity: 1,
																		backgroundColor: 'rgba(0, 0, 0, 0.04)',
																	},
																}}
															>
																<Edit fontSize='small' />
															</Box>
														</Tooltip>
													)}
												</Box>
											)}
										</AccordionSummary>
										<AccordionDetails>
											{/* Кнопка копирования для текущей секции */}
											{fieldSections.length > 0 && (
												<Box sx={{ mb: 2 }}>
													<LinkedFields
														sections={fieldSections}
														values={formik.values}
														onValuesChange={newValues => {
															// Используем setValues для массового обновления
															const updatedValues = {
																...formik.values,
																...newValues,
															}

															formik.setValues(updatedValues)

															// Принудительно обновляем компоненты через изменение key
															setTimeout(() => {
																const autocompleteFields = Object.keys(
																	newValues
																).filter(key => {
																	const field = fields.find(f => f.name === key)
																	return (
																		field?.type === 'autocomplete' &&
																		newValues[key]
																	)
																})

																if (autocompleteFields.length > 0) {
																	// Принудительно перерендериваем форму
																	setSnackbar({
																		open: false,
																		message: '',
																		severity: 'success',
																	})
																}
															}, 200)
														}}
														sourceSection={section.title}
														showInline={true}
													/>
												</Box>
											)}

											<FormSection
												section={section}
												values={formik.values}
												onFieldChange={handleFieldChange}
												getFieldError={getFieldError}
												compact={true}
												showTitle={false}
												preloadedOptions={preloadedOptions}
												isAdminMode={isAdminMode}
												onSectionTitleChange={handleSectionTitleChange}
												showCopyButton={true}
												onClearSection={clearSection}
												sectionIndex={index}
											/>
										</AccordionDetails>
									</Accordion>
								))}

								{/* Кнопка отправки */}
								<Box sx={{ mt: 3 }}>
									<SubmitButton
										submitting={submitting}
										variant='success'
										fullWidth
									/>
								</Box>
							</Box>
						) : (
							// Обычное отображение для коротких форм
							<Box sx={{ p: 3 }}>
								<FormSection
									section={{
										title: form.title,
										fields: getSortedFields(fields),
									}}
									values={formik.values}
									onFieldChange={handleFieldChange}
									getFieldError={getFieldError}
									showTitle={false}
									compact={true}
									preloadedOptions={preloadedOptions}
									isAdminMode={isAdminMode}
									onSectionTitleChange={handleSectionTitleChange}
									onClearSection={clearSection}
									sectionIndex={0}
								/>
								<SubmitButton
									submitting={submitting}
									variant='primary'
									fullWidth
								/>
							</Box>
						)}
					</Box>
				</Paper>
			</Box>

			{/* Кнопка "наверх" */}
			<ScrollToTopButton show={showScrollTop} onClick={scrollToTop} />

			{/* Snackbar для уведомлений */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	)
}

export default BetoneForm
